import { ChevronDown, ChevronRight } from 'lucide-react';
import { getVariantLabel, getVariantStockState, normalizeRequestedQty } from './prModalUtils';

export default function VariantGroup({
  group,
  collapsed,
  onToggle,
  selectedMap,
  onToggleVariant,
  onUpdateVariantQty,
  productThreshold,
}) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 border-b border-neutral-100 px-3 py-2 text-left text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-700"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <span className="truncate">{group.label}</span>
        <span className="ml-auto text-[10px] font-medium text-neutral-500">{group.rows.length}</span>
      </button>

      {!collapsed && (
        <div className="space-y-1.5 p-2">
          {group.rows.map((v) => {
            const label = getVariantLabel(v) || `Variant #${v.id}`;
            const state = getVariantStockState(v, productThreshold);
            const qty = Number(v.quantity ?? 0);
            const isSelected = selectedMap.has(String(v.id));
            const item = selectedMap.get(String(v.id));
            const inactive = v.active === 0;

            const stateCls =
              state === 'NEGATIVE' ? 'text-red-700 dark:text-red-300' :
              state === 'OUT' ? 'text-red-600 dark:text-red-400' :
              state === 'LOW' ? 'text-amber-600 dark:text-amber-400' :
              'text-emerald-700 dark:text-emerald-300';

            const rowCls = isSelected
              ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950/30'
              : inactive
              ? 'border-neutral-200 bg-neutral-50 opacity-60 dark:border-neutral-700 dark:bg-neutral-900/30'
              : state === 'OUT' || state === 'NEGATIVE'
              ? 'border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/20'
              : state === 'LOW'
              ? 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20'
              : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800';

            const stockLabel =
              state === 'NEGATIVE' ? `${qty} NEGATIVE` :
              state === 'OUT' ? `${qty} OUT` :
              state === 'LOW' ? `${qty} LOW` :
              `${qty}`;

            return (
              <div key={v.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${rowCls}`}>
                <input
                  type="checkbox"
                  aria-label={`Select ${label}`}
                  checked={isSelected}
                  disabled={inactive}
                  onChange={() => onToggleVariant(v.id)}
                  className="h-3.5 w-3.5 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                />

                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-neutral-900 dark:text-neutral-100" title={label}>{label}</div>
                  <div className="truncate text-[10px] text-neutral-500 dark:text-neutral-400">
                    {v.sku ? `SKU: ${v.sku}` : 'No SKU'}{inactive ? ' · Inactive variant' : ''}
                  </div>
                </div>

                <span className={`shrink-0 tabular-nums font-semibold ${stateCls}`}>{stockLabel}</span>

                {isSelected && (
                  <div className="flex flex-col items-end gap-0.5">
                    <input
                      type="number"
                      min="1"
                      value={item?.requestedQuantity ?? ''}
                      onChange={(e) => onUpdateVariantQty(v.id, e.target.value)}
                      onBlur={(e) => onUpdateVariantQty(v.id, normalizeRequestedQty(e.target.value))}
                      aria-label={`Requested quantity for ${label}`}
                      placeholder="Qty"
                      className="w-20 rounded-lg border border-neutral-300 bg-white px-2 py-1 text-xs tabular-nums outline-none focus:ring-1 focus:ring-blue-400 dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-100"
                    />
                    {Number(item?.requestedQuantity) < 1 && (
                      <span className="text-[10px] text-red-600 dark:text-red-400">Min 1</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
